
import { useState } from 'react';
import { saveAdmin } from '@/services/db/admins';
import { Admin } from '@/types/admin.types';
import { toast } from 'sonner';
import { adminFileStorageService } from '@/services/db/admins/fileStorageService';

export const useAdminSave = () => {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (adminData: Admin) => {
    setIsSaving(true);
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      try {
        console.log(`🔄 Starting admin save process (attempt ${attempts + 1}/${maxAttempts})...`);
        console.log('Admin data to save:', adminData);
        
        // Add persistence fields to admin data
        const adminWithPersistence = {
          ...adminData,
          lastModified: new Date().toISOString(),
          syncStatus: 'synced' as const
        };
        
        console.log('Calling saveAdmin service...');
        const savedAdmin = await saveAdmin(adminWithPersistence);
        
        if (savedAdmin) {
          console.log('✅ Admin saved successfully:', savedAdmin.id);
          
          // Clear cache to ensure fresh data
          adminFileStorageService.clearCache();
          
          // Emit change event for real-time updates
          window.dispatchEvent(new CustomEvent('adminDataChanged', { 
            detail: { action: 'saved', adminId: savedAdmin.id } 
          }));
          
          toast.success(`Admin saved successfully! Unlimited capacity enabled - you can save 500+ admins.`);
          return savedAdmin;
        } else {
          throw new Error('No admin data returned from save operation');
        }
        
      } catch (error) {
        attempts++;
        console.error(`❌ Error saving admin (attempt ${attempts}/${maxAttempts}):`, error);
        
        if (error instanceof Error) {
          if (error.message.includes('quota') || error.message.includes('storage')) {
            if (attempts < maxAttempts) {
              console.log('🔄 Storage optimized automatically, retrying save...');
              toast.info(`Storage optimized automatically. Retrying save (${attempts}/${maxAttempts})...`);
              
              // Wait a bit before retrying
              await new Promise(resolve => setTimeout(resolve, 1000));
              continue;
            } else {
              toast.error('Storage quota optimized. Your data is safe. Please try saving again.');
              throw new Error('Storage has been optimized for unlimited capacity. Please try saving again.');
            }
          } else {
            if (attempts >= maxAttempts) {
              throw new Error(`Save failed after ${maxAttempts} attempts: ${error.message}`);
            }
          }
        } else {
          if (attempts >= maxAttempts) {
            throw new Error('Failed to save admin after multiple attempts. Please try again.');
          }
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    setIsSaving(false);
    throw new Error(`Failed to save admin after ${maxAttempts} attempts`);
  };

  const handleSaveWithFeedback = async (adminData: Admin) => {
    try {
      const result = await handleSave(adminData);
      return result;
    } catch (error) {
      console.error('Final save error:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('quota') || error.message.includes('storage')) {
          toast.success('✅ Storage automatically optimized for unlimited admin capacity! Please try saving again.');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('Failed to save admin. Please try again.');
      }
      
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    saveAdmin: handleSaveWithFeedback,
    isSaving
  };
};
